import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';

import { outputIfNoVariables } from '../utilities/misc';
import { rem } from '../utilities/style';
import { colors } from '../theme/colors';
import { body, richTextStyles } from '../styles/type';

import Button from './buttons/Button';
import ButtonGroup from './ButtonGroup';
import ButtonText from './buttons/ButtonText';
import Image from './Image';
import TextRenderer from './TextRenderer';
import { fontWeights } from '../theme/type';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Icon = styled(Image)`
  width: ${rem(64)};
  object-fit: contain;
  margin-bottom: ${rem(16)};
`;

const RoundedIcon = styled(Icon)`
  border-radius: 50%;
  height: ${rem(64)};
  object-fit: cover;
`;

const Subhead = styled.p`
  font-size: ${rem(12)};
  font-style: normal;
  font-weight: ${fontWeights.semiBold};
  line-height: 1.55;
  margin-bottom: ${rem(8)};
  padding:${rem(4)} ${rem(8)};
  background-color: ${colors.blue['100']};
  border-radius: ${rem(100)};
  align-self: flex-start;
`;

const Headline = styled.h3`
  font-size: ${rem(20)};
  font-weight: ${fontWeights.bold};
  line-height: 1.55;
  color: ${colors.blue['800']};
  margin-bottom: ${rem(8)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const Text = styled.div`
  ${richTextStyles}
  ${body.regular}
  margin-bottom: auto;
  padding-bottom: ${rem(24)};
`;
const IconCard = ({
  image,
  headline,
  subhead,
  text,
  ctaText,
  ctaLink,
  secondaryCtaText,
  secondaryCtaLink,
  richText = true,
  rounded = false
}) => (
  <Container>
    {image && (
      !rounded ? (
        <Icon image={image} objectFit="contain" />
      ) : (
        <RoundedIcon image={image} objectFit="cover" />
      )
    )}
    {subhead && (
      <Subhead>
        <TextRenderer text={subhead} />
      </Subhead>
    )}
    {headline && (
      <Headline>
        <TextRenderer text={headline} />
      </Headline>
    )}
    {text && (
      <Text>
        <TextRenderer text={text} richText={richText} />
      </Text>
    )}
    <ButtonGroup>
      {ctaText && ctaLink && (
        <Button
          to={ctaLink}
          aria-label={outputIfNoVariables(`${headline} — ${ctaText}`)}
        >
          <TextRenderer text={ctaText} />
        </Button>
      )}
      {secondaryCtaText && secondaryCtaLink && (
        <ButtonText
          to={secondaryCtaLink}
          aria-label={outputIfNoVariables(`${headline} — ${secondaryCtaText}`)}
        >
          <TextRenderer text={secondaryCtaText} />
        </ButtonText>
      )}
    </ButtonGroup>
  </Container>
);

IconCard.propTypes = {
  image: Image.propTypes.image,
  headline: PropTypes.string,
  subhead: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string
  }),
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  rounded: PropTypes.bool
};

export const query = graphql`
  fragment IconCardFragment on ContentfulIconCard {
    id
    image {
      gatsbyImageData(width: 88, height: 88, quality: 75)
      ...ImageAssetFragment
    }
    headline
    subhead
    text {
      raw
    }
    ctaText
    ctaLink
    secondaryCtaText
    secondaryCtaLink
  }
`;

export default IconCard;
